import type { CalendarDate } from '@internationalized/date';
import {
  CalendarStateContext,
  RangeCalendarStateContext,
} from 'react-aria-components';
import { type ReactElement, useContext, useState } from 'react';
import { useDateFormatter } from 'react-aria';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './dropdown-menu';
import { ChevronDownIcon } from 'lucide-react';
import { cn } from './helpers';
import { useScrollToSelectedElement } from './years-dropdown';

interface MonthItem {
  id: number;
  date: CalendarDate;
  formatted: string;
}

export function MonthDropdown(): ReactElement {
  const calendarState = useContext(CalendarStateContext);
  const rangeCalendarState = useContext(RangeCalendarStateContext);
  const state = calendarState || rangeCalendarState!;
  const formatter = useDateFormatter({
    month: 'short',
    timeZone: state.timeZone,
  });

  const months: MonthItem[] = [];
  const numMonths = state.focusedDate.calendar.getMonthsInYear(
    state.focusedDate
  );
  for (let i = 1; i <= numMonths; i++) {
    const date = state.focusedDate.set({ month: i });
    months.push({
      id: i,
      date,
      formatted: formatter.format(date.toDate(state.timeZone)),
    });
  }

  const formattedSelectedMonth = formatter.format(
    state.focusedDate.toDate(state.timeZone)
  );
  const [isOpen, setIsOpen] = useState(false);
  const { containerRef, selectedElRef } = useScrollToSelectedElement(isOpen);

  return (
    <DropdownMenu
      aria-label='Month'
      modal={false}
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      <DropdownMenuTrigger
        onClick={() => setIsOpen(true)}
        className='hover:bg-primary-100 data-[state=open]:bg-primary-100 font-medium flex items-center gap-1 rounded-md h-9 px-2 cursor-pointer'
      >
        {formattedSelectedMonth}
        <ChevronDownIcon size={16} />
      </DropdownMenuTrigger>

      <DropdownMenuContent
        ref={containerRef}
        align='start'
        style={{ scrollbarWidth: 'none' }}
        className='z-[100] h-[200px] overflow-y-auto'
      >
        {months.map((item) => {
          const isSelected = item.date.month === state.focusedDate.month;
          return (
            <DropdownMenuItem
              ref={isSelected ? selectedElRef : undefined}
              onClick={() => state.setFocusedDate(item.date)}
              key={item.id}
              className={cn(
                isSelected &&
                  'bg-primary-100 hover:bg-primary-100 text-primary-900 font-medium'
              )}
            >
              {item.formatted}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
